import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Shield,
  Key,
  Smartphone,
  Monitor,
  Bell,
  Settings2,
  RefreshCw,
  Save,
  LogOut,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  
  // Password change
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // 2FA
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [setupSecret, setSetupSecret] = useState('');
  
  // Sessions and login history
  const [sessions, setSessions] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  
  // Notification preferences
  const [notificationPrefs, setNotificationPrefs] = useState({
    statement_email: true,
    distribution_email: true,
    notice_email: true,
    deal_email: true,
    kyc_reminder_email: true,
    system_alert_email: true,
    email_frequency: 'immediate'
  });
  
  // Display preferences
  const [displayPrefs, setDisplayPrefs] = useState({
    date_format: 'DD/MM/YYYY',
    default_landing_page: '/dashboard'
  });

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [sessionsRes, historyRes, notifRes, displayRes] = await Promise.all([
        axios.get(`${API_URL}/api/settings/sessions`, { headers }),
        axios.get(`${API_URL}/api/settings/sessions`, { headers }),  // Login history from same endpoint for now
        axios.get(`${API_URL}/api/settings/notifications`, { headers }),
        axios.get(`${API_URL}/api/settings/display`, { headers })
      ]);
      
      setSessions(sessionsRes.data);
      setLoginHistory(historyRes.data);
      setNotificationPrefs(notifRes.data);
      setDisplayPrefs(displayRes.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setSaving(prev => ({ ...prev, password: true }));
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API_URL}/api/settings/password`, passwordForm, { headers });
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(prev => ({ ...prev, password: false }));
    }
  };

  const [backupCodes, setBackupCodes] = useState(null); // shown once after enrolment

  const handleSetup2FA = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API_URL}/api/auth/2fa/setup`, {}, { headers });
      setQrCode(`data:image/png;base64,${response.data.qr_png_base64}`);
      setSetupSecret(response.data.secret_plaintext);
      setBackupCodes(null);
      setShow2FADialog(true);
    } catch (error) {
      console.error('Error setting up 2FA:', error);
      toast.error(error.response?.data?.detail || 'Failed to setup 2FA');
    }
  };

  const handleVerify2FA = async () => {
    setSaving(prev => ({ ...prev, totp: true }));
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API_URL}/api/auth/2fa/verify-setup`, { code: totpCode }, { headers });
      toast.success('Two-factor authentication enabled');
      setBackupCodes(res.data.backup_codes || []);
      setTotpCode('');
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setSaving(prev => ({ ...prev, totp: false }));
    }
  };

  const handleDisable2FA = async () => {
    const code = prompt('Enter your current authenticator code to disable 2FA:');
    if (!code) return;
    const pw = prompt('Confirm your account password:');
    if (!pw) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/api/auth/2fa/disable`, { code, password: pw }, { headers });
      toast.success('Two-factor authentication disabled');
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      toast.error(error.response?.data?.detail || 'Invalid code');
    }
  };

  const handleRevokeSession = async (sessionId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_URL}/api/settings/sessions/${sessionId}`, { headers });
      toast.success('Session revoked');
      setSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch (error) {
      console.error('Error revoking session:', error);
      toast.error('Failed to revoke session');
    }
  };

  const handleSaveNotificationPrefs = async () => {
    setSaving(prev => ({ ...prev, notifications: true }));
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API_URL}/api/settings/notifications`, notificationPrefs, { headers });
      toast.success('Notification preferences saved');
    } catch (error) {
      console.error('Error saving notification prefs:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(prev => ({ ...prev, notifications: false }));
    }
  };

  const handleSaveDisplayPrefs = async () => {
    setSaving(prev => ({ ...prev, display: true }));
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API_URL}/api/settings/display`, displayPrefs, { headers });
      toast.success('Display preferences saved');
    } catch (error) {
      console.error('Error saving display prefs:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(prev => ({ ...prev, display: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="settings-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-48 w-full" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#212529] font-['Manrope']">
            Settings
          </h1>
          <p className="text-[#6C757D] mt-1">
            Manage your account security and preferences
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchSettings}
          className="flex items-center gap-2"
          data-testid="refresh-settings-btn"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Security Section */}
      <Card data-testid="security-section">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="h-5 w-5 text-[#1D2D2A]" />
            Security
          </CardTitle>
          <CardDescription>Password and authentication settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Change Password */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Key className="h-4 w-4" />
              Change Password
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password</Label>
                <PasswordInput
                  id="current_password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                  data-testid="input-current-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <PasswordInput
                  id="new_password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                  data-testid="input-new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm Password</Label>
                <PasswordInput
                  id="confirm_password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                  data-testid="input-confirm-password"
                />
              </div>
            </div>
            <Button 
              onClick={handleChangePassword} 
              disabled={saving.password}
              data-testid="change-password-btn"
            >
              {saving.password ? 'Changing...' : 'Change Password'}
            </Button>
          </div>

          <Separator />

          {/* Two-Factor Authentication */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  Two-Factor Authentication
                </h4>
                <p className="text-sm text-gray-500 mt-1">
                  Add an extra layer of security to your account
                </p>
              </div>
              <div className="flex items-center gap-4">
                {user?.totp_enabled ? (
                  <>
                    <Badge variant="default" className="bg-green-100 text-green-800">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Enabled
                    </Badge>
                    <Button variant="outline" onClick={handleDisable2FA} data-testid="disable-2fa-btn">
                      Disable
                    </Button>
                  </>
                ) : (
                  <>
                    <Badge variant="secondary">
                      <XCircle className="h-3 w-3 mr-1" />
                      Disabled
                    </Badge>
                    <Button onClick={handleSetup2FA} data-testid="enable-2fa-btn">
                      Enable 2FA
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Active Sessions */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Monitor className="h-4 w-4" />
              Active Sessions
            </h4>
            <div className="space-y-2">
              {sessions.map((session) => (
                <div 
                  key={session.id} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Monitor className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium">{session.device || 'Unknown Device'}</p>
                      <p className="text-xs text-gray-500">
                        {session.ip_address} • Last active: {formatDate(session.last_active)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {session.is_current && (
                      <Badge variant="secondary" className="bg-green-100 text-green-800">Current</Badge>
                    )}
                    {!session.is_current && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevokeSession(session.id)}
                        data-testid={`revoke-session-${session.id}`}
                      >
                        <LogOut className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {sessions.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">No active sessions found</p>
              )}
            </div>
          </div>

          <Separator />

          {/* Login History */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Login History
            </h4>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs">Date & Time</TableHead>
                    <TableHead className="text-xs">IP Address</TableHead>
                    <TableHead className="text-xs">Device / Browser</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loginHistory.slice(0, 10).map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-sm">{formatDate(entry.created_at)}</TableCell>
                      <TableCell className="text-sm font-mono">{entry.ip_address || '-'}</TableCell>
                      <TableCell className="text-sm truncate max-w-[200px]">{entry.user_agent || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={entry.status === 'success' ? 'default' : 'destructive'} className={
                          entry.status === 'success' ? 'bg-green-100 text-green-800' : ''
                        }>
                          {entry.status === 'success' ? 'Success' : 'Failed'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {loginHistory.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">No login history</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card data-testid="notification-section">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="h-5 w-5 text-[#1D2D2A]" />
            Notification Preferences
          </CardTitle>
          <CardDescription>Control what notifications you receive</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'statement_email', label: 'New Statements', desc: 'When new statements are available' },
              { key: 'distribution_email', label: 'Distributions', desc: 'Distribution payment notifications' },
              { key: 'notice_email', label: 'Notices', desc: 'Important notices and announcements' },
              { key: 'deal_email', label: 'Deal Opportunities', desc: 'New investment opportunities' },
              { key: 'kyc_reminder_email', label: 'KYC Reminders', desc: 'Document expiry reminders' },
              { key: 'system_alert_email', label: 'System Alerts', desc: 'Security and system notifications' },
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
                <Switch
                  checked={notificationPrefs[item.key]}
                  onCheckedChange={(checked) => setNotificationPrefs(prev => ({ ...prev, [item.key]: checked }))}
                  data-testid={`switch-${item.key}`}
                />
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <Label>Email Frequency</Label>
            <Select 
              value={notificationPrefs.email_frequency} 
              onValueChange={(value) => setNotificationPrefs(prev => ({ ...prev, email_frequency: value }))}
            >
              <SelectTrigger className="w-full md:w-64" data-testid="select-email-frequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="immediate">Immediate</SelectItem>
                <SelectItem value="daily">Daily Digest</SelectItem>
                <SelectItem value="weekly">Weekly Digest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button 
            onClick={handleSaveNotificationPrefs}
            disabled={saving.notifications}
            data-testid="save-notifications-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving.notifications ? 'Saving...' : 'Save Preferences'}
          </Button>
        </CardContent>
      </Card>

      {/* Display Preferences */}
      <Card data-testid="display-section">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-[#1D2D2A]" />
            Display Preferences
          </CardTitle>
          <CardDescription>Customize your portal experience</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Date Format</Label>
              <Select 
                value={displayPrefs.date_format} 
                onValueChange={(value) => setDisplayPrefs(prev => ({ ...prev, date_format: value }))}
              >
                <SelectTrigger data-testid="select-date-format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DD/MM/YYYY">DD/MM/YYYY (e.g., 25/12/2024)</SelectItem>
                  <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (e.g., 2024-12-25)</SelectItem>
                  <SelectItem value="MM/DD/YYYY">MM/DD/YYYY (e.g., 12/25/2024)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Default Landing Page</Label>
              <Select 
                value={displayPrefs.default_landing_page} 
                onValueChange={(value) => setDisplayPrefs(prev => ({ ...prev, default_landing_page: value }))}
              >
                <SelectTrigger data-testid="select-landing-page">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="/dashboard">Dashboard</SelectItem>
                  <SelectItem value="/portfolio">Investment Portfolio</SelectItem>
                  <SelectItem value="/distributions">Distributions</SelectItem>
                  <SelectItem value="/deals">Deal Room</SelectItem>
                  <SelectItem value="/reports">Reports & Analytics</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button 
            onClick={handleSaveDisplayPrefs}
            disabled={saving.display}
            data-testid="save-display-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving.display ? 'Saving...' : 'Save Preferences'}
          </Button>
        </CardContent>
      </Card>

      {/* 2FA Setup Dialog */}
      <Dialog open={show2FADialog} onOpenChange={setShow2FADialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Setup Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center space-y-4 py-4">
            {!backupCodes && qrCode && (
              <img src={qrCode} alt="2FA QR Code" className="w-48 h-48 border rounded" />
            )}
            {!backupCodes && (
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Or enter this code manually:</p>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">{setupSecret}</code>
              </div>
            )}
            {!backupCodes && (
              <div className="w-full space-y-2">
                <Label htmlFor="totp_code">Verification Code</Label>
                <Input
                  id="totp_code"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  data-testid="input-totp-code"
                />
              </div>
            )}
            {backupCodes && (
              <div className="w-full space-y-3" data-testid="backup-codes-panel">
                <div className="p-3 rounded border-2 border-emerald-500 bg-emerald-50">
                  <p className="text-sm font-semibold text-emerald-800">✓ 2FA is now active</p>
                  <p className="text-xs text-emerald-700 mt-1">Save these backup codes somewhere safe — this is the ONLY time they&apos;ll be shown. Each code works exactly once.</p>
                </div>
                <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-gray-50 p-3 rounded border">
                  {backupCodes.map((c, i) => (
                    <code key={i} className="text-center py-1" data-testid={`backup-code-${i}`}>{c}</code>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    navigator.clipboard.writeText(backupCodes.join('\n'));
                    toast.success('Backup codes copied to clipboard');
                  }}
                  data-testid="copy-backup-codes"
                >
                  Copy all codes
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            {!backupCodes ? (
              <>
                <Button variant="outline" onClick={() => setShow2FADialog(false)}>Cancel</Button>
                <Button onClick={handleVerify2FA} disabled={saving.totp || totpCode.length !== 6}>
                  {saving.totp ? 'Verifying...' : 'Enable 2FA'}
                </Button>
              </>
            ) : (
              <Button onClick={() => { setShow2FADialog(false); setBackupCodes(null); }} data-testid="close-2fa-dialog">
                I&apos;ve saved my backup codes
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
